<template>
  <div>
    <span class="sr-only">{{ getAriaText(params) }}</span>
    <div v-if="!params.node.group">
      <c-function-button-menu
        :wid="parent.wid"
        :data="params.data"
        :disabledActions="['editAlert']"
        addEmptyRow
        ref="fb_menu"
        @delete="onRemoveSymbol"
        @edit="onEditSymbol"
        @addEmptyRow="onAddEmptyRow"
      >
        <template #misc-dropdown-items>
          <!-- move to group -->
          <v-dropdown-submenu v-if="!params.node.data.index">
            <template #button-content>
              <font-awesome-icon :icon="['fas', 'stream']" />
              <span class="label">{{ i18n('moveToGroup') }}</span>
            </template>
            <v-dropdown-item
              v-for="group in groups"
              :key="group"
              :disabled="params.data.group === group"
              @click="onMoveToGroup(group)"
              >{{ group }}</v-dropdown-item
            >
            <v-dropdown-divider v-if="groups.length" />
            <v-dropdown-item @click="onMoveToGroup">{{ i18n('createGroup') }}</v-dropdown-item>
          </v-dropdown-submenu>
          <v-dropdown-item v-if="params.node.data.group" @click="onRemoveFromGroup">
            <font-awesome-icon :icon="['far', 'undo-alt']" />{{ i18n('removeFromGroup') }}
          </v-dropdown-item>
        </template>
      </c-function-button-menu>
    </div>
    <div v-else>
      <v-dropdown boundary="window" detached no-caret toggle-class="function-button" ref="dropdown" @click.native.stop>
        <template v-slot:button-content>
          <font-awesome-icon :icon="['far', 'ellipsis-v']" />
        </template>
        <template>
          <v-dropdown-item @click="onRenameGroup">
            <font-awesome-icon :icon="['far', 'edit']" />{{ i18n('renameGroup') }}
          </v-dropdown-item>
          <v-dropdown-item v-if="params.node.allLeafChildren[0].data.group" @click="onDissolveGroup">
            <font-awesome-icon :icon="['far', 'times']" />{{ i18n('dissolveGroup') }}
          </v-dropdown-item>
          <v-dropdown-item @click="onRemoveGroup">
            <font-awesome-icon :icon="['far', 'trash-alt']" />{{ i18n('delete') }}
          </v-dropdown-item>
          <v-dropdown-divider />
          <v-dropdown-item @click="onAddEmptyRow">
            <font-awesome-icon :icon="['far', 'plus']" />{{ i18n('addEmptyRow', 'components.functionButtonMenu') }}
          </v-dropdown-item>
        </template>
      </v-dropdown>
    </div>
    <v-dialog ref="createGroupDialog" class="group-name-dialog">
      <v-form-group :state="isValidGroupName" :invalid-feedback="i18n('groupNameDuplicate')">
        <v-form-input
          autocomplete="off"
          autofocus
          :maxlength="$appConfig.generalInputMaxLength"
          :state="isValidGroupName"
          trim
          v-model="inputGroupName"
        />
      </v-form-group>
    </v-dialog>
  </div>
</template>

<script>
import VDialog from '@base/VDialog';
import CFunctionButtonMenu from '@custom/CFunctionButtonMenu';
import { library as fontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { faEllipsisV, faTimes, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { getAriaText } from '@utils/helpers';
fontAwesomeLibrary.add(faEllipsisV, faTimes, faTrashAlt);
export default {
  components: {
    CFunctionButtonMenu,
    VDialog
  },
  data() {
    return {
      inputGroupName: ''
    };
  },
  created() {
    this.params.eGridCell.addEventListener('keydown', this.onKeyDown);
  },
  beforeDestroy() {
    this.params.eGridCell.removeEventListener('keydown', this.onKeyDown);
  },
  computed: {
    isValidGroupName() {
      if (this.params.node.group) {
        return this.inputGroupName === this.params.node.key
          ? undefined
          : this.inputGroupName && !this.groups.includes(this.inputGroupName);
      }
      return !this.inputGroupName.length ? undefined : !this.groups.includes(this.inputGroupName);
    },
    groups() {
      // return unique group names
      return Array.from(new Set(Object.values(this.parent.groups))).sort();
    },
    parent() {
      return this.params.context;
    }
  },
  methods: {
    getAriaText,
    i18n(subkey, ns, params) {
      return this.$t(`white-label.${ns || 'widgets.watchlist.functionButtonMenu'}.${subkey}`, params);
    },
    moveToGroup(group) {
      // find position of the last symbol in the group
      let insertAtPos = Object.entries(this.parent.groups).reduce(
        (pos, [siblingSymbol, siblingGroup]) =>
          siblingGroup === group ? Math.max(pos, this.parent.symbols.indexOf(siblingSymbol) + 1) : pos,
        0
      );
      const nodeData = this.params.data;
      if (insertAtPos && insertAtPos !== nodeData.position) {
        const movingUp = nodeData.position > insertAtPos;
        this.params.api.forEachNode(inode => {
          if (inode === this.params.node || inode.group || inode.data.isFooterRow) return;
          if (movingUp) {
            if (inode.data.position >= insertAtPos && inode.data.position < nodeData.position) inode.data.position++;
          } else if (inode.data.position > nodeData.position && inode.data.position < insertAtPos)
            inode.data.position--;
        });
        nodeData.position = movingUp ? insertAtPos : insertAtPos - 1;
      }
      nodeData.group = group;
      this.params.api.applyTransaction({ update: [nodeData] });
      this.$store.dispatch('watchlists/moveRICToGroup', {
        id: this.parent.controlWatchlist,
        ric: nodeData['q.RIC'],
        group
      });
      return true;
    },
    async onDissolveGroup() {
      // forcing dropdown closure before row removal
      this.$refs.dropdown.hide(false);
      await this.$nextTick();

      const ungroupNodeDatas = this.params.node.allLeafChildren.reduce((acc, cnode) => {
        cnode.data.group = undefined;
        acc.push(cnode.data);
        return acc;
      }, []);
      this.params.api.applyTransaction({ update: ungroupNodeDatas });
      this.$store.dispatch('watchlists/dissolveGroup', {
        id: this.parent.controlWatchlist,
        group: this.params.node.key
      });
    },
    onAddEmptyRow() {
      this.parent.addEmptyRow(this.params.node, this.params.node.group ? null : this.params.data.group);
    },
    onEditSymbol() {
      this.parent.openSymbolSearchDialog(this.$el, this.params.node);
    },
    async onMoveToGroup(group) {
      // forcing dropdown closure before row removal
      this.$refs.fb_menu.$refs.dropdown.hide(false);
      await this.$nextTick();
      if (typeof group === 'string') this.moveToGroup(group);
      else {
        const vm = this;
        this.$refs.createGroupDialog.open({
          buttons: [
            {
              content: ['save'],
              callback: () => this.moveToGroup(this.inputGroupName),
              get disabled() {
                return !vm.isValidGroupName;
              }
            },
            'cancel'
          ],
          focusInput: true,
          header: this.i18n('createGroup'),
          insertTo: this.$el.closest('.widget')
        });
      }
    },
    onRemoveGroup() {
      this.$bvModal
        .msgBoxConfirm(this.i18n('removeGroupConfirm', 'widgets.watchlist.messages', { group: this.params.node.key }), {
          cancelTitle: this.i18n('no', 'globalLabels'),
          centered: true,
          okTitle: this.i18n('yes', 'globalLabels'),
          okVariant: 'danger',
          size: 'sm'
        })
        .then(value => {
          if (value) this.parent.removeSymbols(this.params.node);
        });
    },
    async onRemoveSymbol() {
      // forcing dropdown closure before row removal
      this.$refs.fb_menu.$refs.dropdown.hide(false);
      await this.$nextTick();
      this.parent.removeSymbols(this.params.node);
    },
    onRenameGroup() {
      const vm = this;
      this.inputGroupName = this.params.node.key;
      this.$refs.createGroupDialog.open({
        buttons: [
          {
            content: ['save'],
            callback: () => {
              const groupNodesDatas = this.params.node.allLeafChildren.reduce((acc, rowNode) => {
                rowNode.data.group = this.inputGroupName;
                acc.push(rowNode.data);
                return acc;
              }, []);
              this.params.api.applyTransaction({ update: groupNodesDatas });
              this.$store.dispatch('watchlists/renameGroup', {
                id: this.parent.controlWatchlist,
                group: this.params.node.key,
                newName: this.inputGroupName
              });
              return true;
            },
            get disabled() {
              return !vm.isValidGroupName;
            }
          },
          'cancel'
        ],
        focusInput: true,
        header: this.i18n('renameGroup'),
        insertTo: this.$el.closest('.widget')
      });
    },
    async onRemoveFromGroup() {
      // forcing dropdown closure before row removal
      this.$refs.fb_menu.$refs.dropdown.hide(false);
      await this.$nextTick();
      const nodeData = this.params.data;
      nodeData.group = undefined;
      this.params.api.applyTransaction({ update: [nodeData] });
      this.$store.dispatch('watchlists/removeRICFromGroup', {
        id: this.parent.controlWatchlist,
        ric: nodeData['q.RIC']
      });
    },
    onKeyDown(e) {
      const enter = e.keyCode === 13;
      const fwdTab = e.keyCode === 9 && !e.shiftKey;
      const buttonInFocus = e.target.tagName === 'BUTTON';
      if (!buttonInFocus && (fwdTab || enter)) {
        this.$refs.dropdown.$el.querySelector('button').focus();
        e.preventDefault();
      }
    }
  }
};
</script>

<style lang="scss">
.ag-body-viewport {
  .function-button {
    width: 1.5em;
    height: 1.5em;
    border-radius: 50%;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: none;
    border: none;
  }
}
</style>
